import {
  BuildShipmentTargetRequestSchema,
  BuildShipmentTargetResponseSchema,
  CreateShipmentQuickCaptureEventRequestSchema,
  CreateShipmentQuickCaptureEventResponseSchema,
  CreateShipmentWeighCaptureLinkRequestSchema,
  CreateShipmentWeighCaptureLinkResponseSchema,
  CreateShipmentDraftRequestSchema,
  CreateShipmentDraftResponseSchema,
  ExportShipmentDailyRegularRequestSchema,
  ExportShipmentDailyRegularResponseSchema,
  GetAssignedShipmentQuickCaptureAvailabilityRequestSchema,
  GetAssignedShipmentQuickCaptureAvailabilityResponseSchema,
  GetShipmentRequestSchema,
  GetShipmentResponseSchema,
  ListShipmentWeighCaptureLinkTargetsRequestSchema,
  ListShipmentWeighCaptureLinkTargetsResponseSchema,
  ListShipmentWeighCaptureLinksRequestSchema,
  ListShipmentWeighCaptureLinksResponseSchema,
  ListShipmentQuickCaptureTargetsRequestSchema,
  ListShipmentQuickCaptureTargetsResponseSchema,
  ListShipmentCandidateLinesRequestSchema,
  ListShipmentCandidateLinesResponseSchema,
  ListShipmentsRequestSchema,
  ListShipmentsResponseSchema,
  MarkShipmentDepartedRequestSchema,
  MarkShipmentDepartedResponseSchema,
  SaveShipmentRequestSchema,
  SaveShipmentResponseSchema,
  RecoverShipmentQuickCaptureEventRequestSchema,
  RecoverShipmentQuickCaptureEventResponseSchema,
  RecoverShipmentWeighCaptureLinkRequestSchema,
  RecoverShipmentWeighCaptureLinkResponseSchema,
  ResolveRcScopeRequestSchema,
  ResolveRcScopeResponseSchema,
  ResolveShipmentWeighCaptureLinkRequestSchema,
  ResolveShipmentWeighCaptureLinkResponseSchema,
  RevokeShipmentWeighCaptureLinkRequestSchema,
  RevokeShipmentWeighCaptureLinkResponseSchema,
  SubmitShipmentWeighCaptureLinkRequestSchema,
  SubmitShipmentWeighCaptureLinkResponseSchema,
  UpdateShipmentDraftRequestSchema,
  UpdateShipmentDraftResponseSchema,
  ValidateShipmentRangeRequestSchema,
  ValidateShipmentRangeResponseSchema,
  VoidShipmentRequestSchema,
  VoidShipmentResponseSchema,
  buildShipmentEvidenceSummaryFromLoadSessionDetail,
  type BuildShipmentTargetRequest,
  type BuildShipmentTargetResponse,
  type CreateShipmentQuickCaptureEventRequest,
  type CreateShipmentQuickCaptureEventResponse,
  type CreateShipmentWeighCaptureLinkRequest,
  type CreateShipmentWeighCaptureLinkResponse,
  type CreateShipmentDraftRequest,
  type CreateShipmentDraftResponse,
  type ExportShipmentDailyRegularRequest,
  type ExportShipmentDailyRegularResponse,
  type GetAssignedShipmentQuickCaptureAvailabilityRequest,
  type GetAssignedShipmentQuickCaptureAvailabilityResponse,
  type GetShipmentRequest,
  type GetShipmentResponse,
  type ListShipmentWeighCaptureLinkTargetsRequest,
  type ListShipmentWeighCaptureLinkTargetsResponse,
  type ListShipmentWeighCaptureLinksRequest,
  type ListShipmentWeighCaptureLinksResponse,
  type ListShipmentQuickCaptureTargetsRequest,
  type ListShipmentQuickCaptureTargetsResponse,
  type ListShipmentCandidateLinesRequest,
  type ListShipmentCandidateLinesResponse,
  type ListShipmentsRequest,
  type ListShipmentsResponse,
  type MarkShipmentDepartedRequest,
  type MarkShipmentDepartedResponse,
  type RecoverShipmentQuickCaptureEventRequest,
  type RecoverShipmentQuickCaptureEventResponse,
  type RecoverShipmentWeighCaptureLinkRequest,
  type RecoverShipmentWeighCaptureLinkResponse,
  type SaveShipmentRequest,
  type SaveShipmentResponse,
  type ShipmentEvidenceSummary,
  type ResolveRcScopeRequest,
  type ResolveRcScopeResponse,
  type ResolveShipmentWeighCaptureLinkRequest,
  type ResolveShipmentWeighCaptureLinkResponse,
  type RevokeShipmentWeighCaptureLinkRequest,
  type RevokeShipmentWeighCaptureLinkResponse,
  type SubmitShipmentWeighCaptureLinkRequest,
  type SubmitShipmentWeighCaptureLinkResponse,
  type UpdateShipmentDraftRequest,
  type UpdateShipmentDraftResponse,
  type ValidateShipmentRangeRequest,
  type ValidateShipmentRangeResponse,
  type VoidShipmentRequest,
  type VoidShipmentResponse,
} from '@crv/contracts/shipments';
import { z } from 'zod';

import { getHoldingUnitLoadSessionDetail } from '../holding-units/holding-units.api';
import { apiPost } from '../http-client';

const ShipmentEvidenceSummaryRequestSchema = z.object({
  companyId: z.uuid(),
  loadSessionId: z.uuid(),
  groupId: z.uuid(),
});

export type ShipmentEvidenceSummaryRequest = z.infer<typeof ShipmentEvidenceSummaryRequestSchema>;

export type ShipmentEvidenceSummaryResponse = ShipmentEvidenceSummary;

export async function resolveRcScope(
  request: ResolveRcScopeRequest,
): Promise<ResolveRcScopeResponse> {
  const payload = ResolveRcScopeRequestSchema.parse(request);
  const path = `/v1/companies/${payload.companyId}/shipments/resolve-rc-scope`;
  return apiPost(path, ResolveRcScopeResponseSchema, payload);
}

export async function validateShipmentRange(
  request: ValidateShipmentRangeRequest,
): Promise<ValidateShipmentRangeResponse> {
  const payload = ValidateShipmentRangeRequestSchema.parse(request);
  const path = `/v1/companies/${payload.companyId}/shipments/validate-range`;
  return apiPost(path, ValidateShipmentRangeResponseSchema, payload);
}

export async function listShipmentCandidateLines(
  request: ListShipmentCandidateLinesRequest,
): Promise<ListShipmentCandidateLinesResponse> {
  const payload = ListShipmentCandidateLinesRequestSchema.parse(request);
  const path = `/v1/companies/${payload.companyId}/shipments/list-candidate-lines`;
  return apiPost(path, ListShipmentCandidateLinesResponseSchema, payload);
}

export async function listShipments(request: ListShipmentsRequest): Promise<ListShipmentsResponse> {
  const payload = ListShipmentsRequestSchema.parse(request);
  const path = `/v1/companies/${payload.companyId}/shipments/list`;
  return apiPost(path, ListShipmentsResponseSchema, payload);
}

export async function listShipmentQuickCaptureTargets(
  request: ListShipmentQuickCaptureTargetsRequest,
): Promise<ListShipmentQuickCaptureTargetsResponse> {
  const payload = ListShipmentQuickCaptureTargetsRequestSchema.parse(request);
  const path = `/v1/companies/${payload.companyId}/shipments/quick-capture`;
  return apiPost(path, ListShipmentQuickCaptureTargetsResponseSchema, payload);
}

export async function getAssignedShipmentQuickCaptureAvailability(
  request: GetAssignedShipmentQuickCaptureAvailabilityRequest,
): Promise<GetAssignedShipmentQuickCaptureAvailabilityResponse> {
  const payload = GetAssignedShipmentQuickCaptureAvailabilityRequestSchema.parse(request);
  const path = `/v1/companies/${payload.companyId}/shipments/quick-capture/assigned-availability`;
  return apiPost(path, GetAssignedShipmentQuickCaptureAvailabilityResponseSchema, payload);
}

export async function createShipmentQuickCaptureEvent(
  request: CreateShipmentQuickCaptureEventRequest,
): Promise<CreateShipmentQuickCaptureEventResponse> {
  const payload = CreateShipmentQuickCaptureEventRequestSchema.parse(request);
  const path = `/v1/companies/${payload.companyId}/shipments/quick-capture/create-weigh-event`;
  return apiPost(path, CreateShipmentQuickCaptureEventResponseSchema, payload);
}

export async function recoverShipmentQuickCaptureEvent(
  request: RecoverShipmentQuickCaptureEventRequest,
): Promise<RecoverShipmentQuickCaptureEventResponse> {
  const payload = RecoverShipmentQuickCaptureEventRequestSchema.parse(request);
  const path = `/v1/companies/${payload.companyId}/shipments/quick-capture/recover-weigh-event`;
  return apiPost(path, RecoverShipmentQuickCaptureEventResponseSchema, payload);
}

export async function listShipmentWeighCaptureLinkTargets(
  request: ListShipmentWeighCaptureLinkTargetsRequest,
): Promise<ListShipmentWeighCaptureLinkTargetsResponse> {
  const payload = ListShipmentWeighCaptureLinkTargetsRequestSchema.parse(request);
  const path = `/v1/companies/${payload.companyId}/shipments/secure-capture-links/targets`;
  return apiPost(path, ListShipmentWeighCaptureLinkTargetsResponseSchema, payload);
}

export async function listShipmentWeighCaptureLinks(
  request: ListShipmentWeighCaptureLinksRequest,
): Promise<ListShipmentWeighCaptureLinksResponse> {
  const payload = ListShipmentWeighCaptureLinksRequestSchema.parse(request);
  const path = `/v1/companies/${payload.companyId}/shipments/secure-capture-links/list`;
  return apiPost(path, ListShipmentWeighCaptureLinksResponseSchema, payload);
}

export async function createShipmentWeighCaptureLink(
  request: CreateShipmentWeighCaptureLinkRequest,
): Promise<CreateShipmentWeighCaptureLinkResponse> {
  const payload = CreateShipmentWeighCaptureLinkRequestSchema.parse(request);
  const path = `/v1/companies/${payload.companyId}/shipments/secure-capture-links/create`;
  return apiPost(path, CreateShipmentWeighCaptureLinkResponseSchema, payload);
}

export async function revokeShipmentWeighCaptureLink(
  request: RevokeShipmentWeighCaptureLinkRequest,
): Promise<RevokeShipmentWeighCaptureLinkResponse> {
  const payload = RevokeShipmentWeighCaptureLinkRequestSchema.parse(request);
  const path = `/v1/companies/${payload.companyId}/shipments/secure-capture-links/revoke`;
  return apiPost(path, RevokeShipmentWeighCaptureLinkResponseSchema, payload);
}

export async function resolveShipmentWeighCaptureLink(
  request: ResolveShipmentWeighCaptureLinkRequest,
): Promise<ResolveShipmentWeighCaptureLinkResponse> {
  const payload = ResolveShipmentWeighCaptureLinkRequestSchema.parse(request);
  const path = `/v1/public/shipment-weigh-capture-links/${payload.linkId}/resolve`;
  return apiPost(path, ResolveShipmentWeighCaptureLinkResponseSchema, payload);
}

export async function submitShipmentWeighCaptureLink(
  request: SubmitShipmentWeighCaptureLinkRequest,
): Promise<SubmitShipmentWeighCaptureLinkResponse> {
  const payload = SubmitShipmentWeighCaptureLinkRequestSchema.parse(request);
  const path = `/v1/public/shipment-weigh-capture-links/${payload.linkId}/submit`;
  return apiPost(path, SubmitShipmentWeighCaptureLinkResponseSchema, payload);
}

export async function recoverShipmentWeighCaptureLink(
  request: RecoverShipmentWeighCaptureLinkRequest,
): Promise<RecoverShipmentWeighCaptureLinkResponse> {
  const payload = RecoverShipmentWeighCaptureLinkRequestSchema.parse(request);
  const path = `/v1/public/shipment-weigh-capture-links/${payload.linkId}/recover`;
  return apiPost(path, RecoverShipmentWeighCaptureLinkResponseSchema, payload);
}

export async function getShipment(request: GetShipmentRequest): Promise<GetShipmentResponse> {
  const payload = GetShipmentRequestSchema.parse(request);
  const path = `/v1/companies/${payload.companyId}/shipments/get`;
  return apiPost(path, GetShipmentResponseSchema, payload);
}

export async function buildShipmentTarget(
  request: BuildShipmentTargetRequest,
): Promise<BuildShipmentTargetResponse> {
  const payload = BuildShipmentTargetRequestSchema.parse(request);
  const path = `/v1/companies/${payload.companyId}/shipments/build-target`;
  return apiPost(path, BuildShipmentTargetResponseSchema, payload);
}

export async function createShipmentDraft(
  request: CreateShipmentDraftRequest,
): Promise<CreateShipmentDraftResponse> {
  const payload = CreateShipmentDraftRequestSchema.parse(request);
  const path = `/v1/companies/${payload.companyId}/shipments/create-draft`;
  return apiPost(path, CreateShipmentDraftResponseSchema, payload);
}

export async function updateShipmentDraft(
  request: UpdateShipmentDraftRequest,
): Promise<UpdateShipmentDraftResponse> {
  const payload = UpdateShipmentDraftRequestSchema.parse(request);
  const path = `/v1/companies/${payload.companyId}/shipments/update-draft`;
  return apiPost(path, UpdateShipmentDraftResponseSchema, payload);
}

export async function saveShipment(request: SaveShipmentRequest): Promise<SaveShipmentResponse> {
  const payload = SaveShipmentRequestSchema.parse(request);
  const path = `/v1/companies/${payload.companyId}/shipments/save`;
  return apiPost(path, SaveShipmentResponseSchema, payload);
}

export async function markShipmentDeparted(
  request: MarkShipmentDepartedRequest,
): Promise<MarkShipmentDepartedResponse> {
  const payload = MarkShipmentDepartedRequestSchema.parse(request);
  const path = `/v1/companies/${payload.companyId}/shipments/mark-departed`;
  return apiPost(path, MarkShipmentDepartedResponseSchema, payload);
}

export async function voidShipment(request: VoidShipmentRequest): Promise<VoidShipmentResponse> {
  const payload = VoidShipmentRequestSchema.parse(request);
  const path = `/v1/companies/${payload.companyId}/shipments/void`;
  return apiPost(path, VoidShipmentResponseSchema, payload);
}

export async function exportShipmentDailyRegular(
  request: ExportShipmentDailyRegularRequest,
): Promise<ExportShipmentDailyRegularResponse> {
  const payload = ExportShipmentDailyRegularRequestSchema.parse(request);
  const path = `/v1/companies/${payload.companyId}/shipments/export-daily-regular`;
  return apiPost(path, ExportShipmentDailyRegularResponseSchema, payload);
}

export async function getShipmentEvidenceSummary(
  request: ShipmentEvidenceSummaryRequest,
): Promise<ShipmentEvidenceSummaryResponse> {
  const params = ShipmentEvidenceSummaryRequestSchema.parse(request);
  const detail = await getHoldingUnitLoadSessionDetail({
    companyId: params.companyId,
    loadSessionId: params.loadSessionId,
  });
  return buildShipmentEvidenceSummaryFromLoadSessionDetail(detail, params.groupId);
}
